import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    ArrowOutward as SendIcon,
    CallReceived as ReceiveIcon,
    Download as DownloadIcon,
    ReceiptLong as ReceiptIcon,
    Search as SearchIcon,
    ShieldOutlined as ShieldIcon,
    SwapHoriz as SwapIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTransactionsSafe } from '../contexts/TransactionsContext'
import { useLanguage } from '../contexts/LanguageContext'

const BRAND_ORANGE = '#FF8A00'
const SURFACE = 'rgba(16, 11, 38, 0.92)'
const SURFACE_SOFT = 'rgba(36, 23, 74, 0.9)'
const BORDER = '1px solid rgba(255,255,255,0.08)'

const cardSx = {
    borderRadius: 6,
    background: SURFACE,
    border: BORDER,
    boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
}

const formatMoney = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
    }).format(Number(value || 0))

const formatDate = (value, lang = 'en') => {
    if (!value) return lang === 'fr' ? 'A l instant' : 'Just now'
    try {
        return new Date(value).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        })
    } catch (error) {
        return 'Just now'
    }
}

const normalizeType = (type = '') => String(type || '').toLowerCase().replace(/[\s-]+/g, '_')

const typeFilterOptions = [
    { value: 'all', en: 'All types', fr: 'Tous types', matches: [] },
    { value: 'send', en: 'Send', fr: 'Envoi', matches: ['send', 'wallet_transfer', 'transfer', 'remittance', 'payout'] },
    { value: 'receive', en: 'Receive', fr: 'Reception', matches: ['receive', 'received'] },
    { value: 'funding', en: 'Wallet funding', fr: 'Recharge wallet', matches: ['funding', 'wallet_funding', 'deposit', 'top_up', 'topup'] },
    { value: 'payment', en: 'Merchant payment', fr: 'Paiement commercant', matches: ['payment', 'merchant', 'service_payment', 'qr_payment'] },
    { value: 'bill', en: 'Bill pay', fr: 'Paiement facture', matches: ['bill', 'bill_payment', 'utility'] },
    { value: 'tax', en: 'Tax', fr: 'Taxe', matches: ['tax', 'tax_payment', 'permit', 'government'] },
    { value: 'invoice', en: 'Invoice', fr: 'Facture client', matches: ['invoice', 'invoice_payment', 'pay_invoice'] },
    { value: 'donation', en: 'Donation', fr: 'Don', matches: ['donation', 'donate', 'giving'] },
    { value: 'contribution', en: 'Contribution / Osusu', fr: 'Contribution / Osusu', matches: ['contribution', 'osusu', 'savings', 'community_fund'] },
    { value: 'ticket', en: 'Tickets', fr: 'Billets', matches: ['ticket', 'event', 'pass'] },
    { value: 'bank', en: 'Bank transfer', fr: 'Virement bancaire', matches: ['bank', 'bank_transfer'] },
]

const searchableTypeText = (item = {}) => {
    const raw = item.raw || {}
    const meta = item.meta || raw.meta || {}
    const breakdown = item.breakdown || raw.breakdown || {}

    return [
        item.type,
        item.note,
        item.counterpart,
        item.method,
        item.title,
        item.description,
        raw.type,
        raw.note,
        raw.remark,
        raw.description,
        raw.service_domain,
        raw.transaction_classification,
        raw.payment_category,
        raw.payment_type,
        raw.receipt_type,
        meta.receiptType,
        meta.service_domain,
        breakdown.receiptType,
        breakdown.service_domain,
    ]
        .filter(Boolean)
        .map(normalizeType)
        .join(' ')
}

const matchesTypeFilter = (item, filter) => {
    if (filter === 'all') return true

    const option = typeFilterOptions.find((entry) => entry.value === filter)
    const haystack = searchableTypeText(item)
    const needles = option?.matches || [filter]

    return needles.some((needle) => haystack.includes(normalizeType(needle)))
}

const localizeTransactionText = (value, lang) => {
    const text = String(value || '').trim()
    if (lang !== 'fr' || !text) return text

    const normalized = text.toLowerCase().replace(/_/g, ' ')
    const labels = {
        received: 'Recu',
        sent: 'Envoye',
        completed: 'Termine',
        pending: 'En attente',
        failed: 'Echec',
        'pay invoice': 'Paiement de facture',
        'invoice payment': 'Paiement de facture',
        'payment completed': 'Paiement termine',
        'service payment': 'Paiement service',
        'merchant payment': 'Paiement commercant',
        donation: 'Don',
        'donation payment': 'Don',
        'contribution payment': 'Contribution',
        'osusu contribution': 'Contribution Osusu',
        'osusu circle created': 'Cercle Osusu cree',
        'community fund contribution': 'Contribution communautaire',
        ticket: 'Billet',
        'ticket payment': 'Paiement billet',
        'bank transfer': 'Virement bancaire',
        'wallet transfer sent': 'Transfert wallet envoye',
        'wallet transfer received': 'Transfert wallet recu',
        funding: 'Recharge',
        deposit: 'Depot',
        withdrawal: 'Retrait',
        withdraw: 'Retrait',
    }

    return labels[normalized] || text
}

const escapeCsv = (value) => {
    const text = String(value ?? '')
    if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
    }
    return text
}

const statusLabel = (status, lang) => {
    const normalized = String(status || 'completed').toLowerCase()
    if (normalized.includes('pend')) return lang === 'fr' ? 'En attente' : 'pending'
    if (normalized.includes('fail') || normalized.includes('reject') || normalized.includes('cancel')) {
        return lang === 'fr' ? 'Echec' : 'failed'
    }
    return lang === 'fr' ? 'Termine' : 'completed'
}

const getTypeMeta = (type = '', lang = 'en') => {
    const normalized = normalizeType(type)

    if (normalized.includes('donation') || normalized.includes('donate')) {
        return {
            label: lang === 'fr' ? 'Don' : 'Donation',
            icon: <ReceiptIcon />,
            accent: '#A78BFA',
            description: lang === 'fr' ? 'Recu de don' : 'Donation receipt',
        }
    }

    if (
        ['contribution', 'osusu', 'community_fund', 'savings'].some((value) =>
            normalized.includes(value)
        )
    ) {
        return {
            label: lang === 'fr' ? 'Contribution' : 'Contribution',
            icon: <SwapIcon />,
            accent: '#F59E0B',
            description: lang === 'fr' ? 'Contribution Osusu ou communautaire' : 'Osusu or community contribution',
        }
    }

    if (normalized.includes('ticket') || normalized.includes('event') || normalized.includes('pass')) {
        return {
            label: lang === 'fr' ? 'Billet' : 'Ticket',
            icon: <ReceiptIcon />,
            accent: '#FB7185',
            description: lang === 'fr' ? 'Preuve de billet ou evenement' : 'Ticket or event proof',
        }
    }

    if (normalized.includes('bank_transfer') || normalized.includes('bank')) {
        return {
            label: lang === 'fr' ? 'Banque' : 'Bank',
            icon: <SendIcon />,
            accent: BRAND_ORANGE,
            description: lang === 'fr' ? 'Virement bancaire' : 'Bank transfer',
        }
    }

    if (
        ['send', 'transfer', 'bank_transfer', 'remittance'].some((value) =>
            normalized.includes(value)
        )
    ) {
        return {
            label: lang === 'fr' ? 'Envoi' : 'Send',
            icon: <SendIcon />,
            accent: BRAND_ORANGE,
            description: lang === 'fr' ? 'Envoi transfrontalier ou paiement' : 'Cross-border send or payout',
        }
    }

    if (
        ['receive', 'funding', 'wallet_funding', 'deposit'].some((value) =>
            normalized.includes(value)
        )
    ) {
        return {
            label: lang === 'fr' ? 'Recharge' : 'Funding',
            icon: <ReceiveIcon />,
            accent: '#36D399',
            description: lang === 'fr' ? 'Argent ajoute ou recu' : 'Money added or received',
        }
    }

    if (
        ['bill', 'invoice', 'tax', 'donation', 'payment', 'merchant'].some((value) =>
            normalized.includes(value)
        )
    ) {
        return {
            label: lang === 'fr' ? 'Recu' : 'Receipt',
            icon: <ReceiptIcon />,
            accent: '#8B5CF6',
            description: lang === 'fr' ? 'Preuve de paiement ou service' : 'Proof of payment or service',
        }
    }

    return {
        label: lang === 'fr' ? 'Activite' : 'Activity',
        icon: <SwapIcon />,
        accent: '#60A5FA',
        description: lang === 'fr' ? 'Activite wallet et compte' : 'Wallet and account activity',
    }
}

const Transactions = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { lang } = useLanguage()
    const transactionsContext = useTransactionsSafe()
    const hasSyncedTransactions = useRef(false)
    const liveTransactions = useMemo(
        () => (Array.isArray(transactionsContext?.transactions) ? transactionsContext.transactions : []),
        [transactionsContext?.transactions]
    )

    const [query, setQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')

    useEffect(() => {
        if (
            hasSyncedTransactions.current ||
            !transactionsContext ||
            typeof transactionsContext.loadFromApi !== 'function'
        ) {
            return
        }

        hasSyncedTransactions.current = true
        transactionsContext.loadFromApi().catch(() => {
            hasSyncedTransactions.current = false
        })
    }, [transactionsContext])

    const isBusiness = ['merchant', 'business'].includes(
        String(user?.role || user?.type || '').toLowerCase()
    ) || user?.account_type === 'business' || user?.is_business === true || !!user?.business_name

    const isDiaspora = ['US', 'CA', 'UK', 'GB', 'AU', 'FR', 'EU'].includes(
        String(user?.country || '').toUpperCase()
    )
    const fallbackCurrency = String(user?.baseCurrency || 'USD').toUpperCase()

    const items = useMemo(() => (
        liveTransactions.length > 0 ? liveTransactions : []
    ), [liveTransactions])

    const filteredItems = useMemo(() => {
        return items.filter((item) => {
            const matchesQuery = `${item.id} ${item.type} ${item.counterpart || ''} ${item.note || ''}`
                .toLowerCase()
                .includes(query.trim().toLowerCase())

            const matchesStatus =
                statusFilter === 'all' ||
                String(item.status || '').toLowerCase() === statusFilter

            const matchesType = matchesTypeFilter(item, typeFilter)

            return matchesQuery && matchesStatus && matchesType
        })
    }, [items, query, statusFilter, typeFilter])

    const stats = useMemo(() => {
        return filteredItems.reduce(
            (acc, item) => {
                const amount = Number(item.amount || 0)
                const fee = Number(item.fee || 0)
                const status = String(item.status || '').toLowerCase()
                const type = normalizeType(item.type)

                acc.totalFees += fee

                if (status === 'pending') {
                    acc.pending += amount
                }

                if (['receive', 'funding', 'deposit'].some((value) => type.includes(value))) {
                    acc.received += amount
                } else {
                    acc.sent += amount
                }

                return acc
            },
            { sent: 0, received: 0, pending: 0, totalFees: 0 }
        )
    }, [filteredItems])

    const handleExport = () => {
        const csv = [
            ['Reference', 'Type', 'Status', 'Counterpart', 'Note', 'Amount', 'Fee', 'Currency', 'Created At'],
            ...filteredItems.map((item) => ([
                item.id,
                localizeTransactionText(item.type, lang),
                statusLabel(item.status, lang),
                localizeTransactionText(item.counterpart || '', lang),
                localizeTransactionText(item.note || '', lang),
                Number(item.amount || 0).toFixed(2),
                Number(item.fee || 0).toFixed(2),
                String(item.currency || fallbackCurrency).toUpperCase(),
                item.createdAt || '',
            ])),
        ]
            .map((row) => row.map(escapeCsv).join(','))
            .join('\n')

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', `mojopayr-transactions-${new Date().toISOString().slice(0, 10)}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    const insightCards = [
        {
            title: isBusiness
                ? (lang === 'fr' ? 'Encaisse' : 'Collected')
                : 'Money sent',
            value: formatMoney(stats.sent, fallbackCurrency),
            note: isBusiness
                ? (lang === 'fr'
                    ? 'Encaissements et reglements confirmes'
                    : 'Confirmed collections and settlements')
                : isDiaspora
                    ? (lang === 'fr' ? 'Transferts et paiements diaspora' : 'Diaspora transfers and payments')
                    : (lang === 'fr' ? 'Paiements locaux et usage quotidien' : 'Local pay-outs and daily usage'),
        },
        {
            title: isBusiness
                ? (lang === 'fr' ? 'Recu' : 'Received')
                : (lang === 'fr' ? 'Argent recu' : 'Money received'),
            value: formatMoney(stats.received, fallbackCurrency),
            note: isBusiness
                ? (lang === 'fr' ? 'Paiements clients et entrees de compte' : 'Customer payments and inbound activity')
                : (lang === 'fr' ? 'Approvisionnement du portefeuille et activite entrante' : 'Wallet funding and inbound activity'),
        },
        {
            title: lang === 'fr' ? 'En attente de revue' : 'Pending review',
            value: formatMoney(stats.pending, fallbackCurrency),
            note: lang === 'fr' ? 'Elements en attente de reseau ou de confirmation de reglement' : 'Items waiting on network or settlement confirmation',
        },
        {
            title: lang === 'fr' ? 'Preuves pretes' : 'Proof ready',
            value: String(filteredItems.filter((item) => String(item.status || '').toLowerCase() === 'completed').length),
            note: lang === 'fr' ? 'Recus completes et activite confirmee' : 'Completed receipts and confirmed activity',
        },
    ]

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Card
                    sx={{
                        ...cardSx,
                        mb: 3,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(59,27,130,0.96) 0%, rgba(92,44,222,0.95) 58%, rgba(255,138,0,0.18) 100%)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                        >
                            <Box>
                                <Chip
                                    label={isBusiness
                                        ? (lang === 'fr' ? 'Profil entreprise' : 'Business profile')
                                        : isDiaspora
                                            ? (lang === 'fr' ? 'Profil diaspora' : 'Diaspora profile')
                                            : (lang === 'fr' ? 'Profil local' : 'Local profile')}
                                    sx={{
                                        mb: 2,
                                        bgcolor: 'rgba(255,255,255,0.12)',
                                        color: '#fff',
                                        fontWeight: 800,
                                    }}
                                />
                                <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 30, md: 42 } }}>
                                    {lang === 'fr' ? 'Transactions' : 'Transactions'}
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 760, mt: 1.2 }}>
                                    {isBusiness
                                        ? (lang === 'fr'
                                            ? 'Suivez les encaissements, preuves, factures et reglements dans un espace plus clair pour l activite de votre entreprise.'
                                            : 'Follow collections, proof, invoices, and settlements in one cleaner place for your business.')
                                        : (lang === 'fr'
                                            ? 'Suivez chaque envoi, facture, recu, don, et reglement dans un seul espace clair. MojoPayr garde la piste de preuve visible sans surcharger la page.'
                                            : 'Follow every send, bill, receipt, donation, and settlement in one clean place. MojoPayr keeps the proof trail visible without overwhelming the page.')}
                                </Typography>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                                <Button variant="contained" onClick={() => navigate(isBusiness ? '/invoices/create' : '/send')} sx={{ borderRadius: 999, px: 2.4, py: 1.1, fontWeight: 900, textTransform: 'none', bgcolor: '#fff', color: '#130A32', '&:hover': { bgcolor: '#fff' } }}>
                                    {isBusiness
                                        ? (lang === 'fr' ? 'Creer facture' : 'Create invoice')
                                        : (lang === 'fr' ? 'Envoyer encore' : 'Send again')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleExport}
                                    disabled={filteredItems.length === 0}
                                    startIcon={<DownloadIcon />}
                                    sx={{
                                        borderRadius: 999,
                                        px: 2.4,
                                        py: 1.1,
                                        fontWeight: 900,
                                        textTransform: 'none',
                                        color: '#fff',
                                        borderColor: 'rgba(255,255,255,0.34)',
                                    }}
                                >
                                    {lang === 'fr' ? 'Exporter CSV' : 'Export CSV'}
                                </Button>
                                <Button variant="outlined" onClick={() => navigate('/help-center')} sx={{ borderRadius: 999, px: 2.4, py: 1.1, fontWeight: 900, textTransform: 'none', color: '#fff', borderColor: 'rgba(255,255,255,0.34)' }}>
                                    {isBusiness
                                        ? (lang === 'fr' ? 'Support entreprise' : 'Business help')
                                        : (lang === 'fr' ? 'Besoin d aide ?' : 'Need help?')}
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {insightCards.map((card) => (
                        <Grid item xs={12} sm={6} md={3} key={card.title}>
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 2.6 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13 }}>{card.title}</Typography>
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 28, mt: 0.8 }}>{card.value}</Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 12.5, mt: 1 }}>{card.note}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2.2 }}>
                                    <Box>
                                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>{lang === 'fr' ? 'Piste de paiement' : 'Payment trail'}</Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.62)', mt: 0.6 }}>{lang === 'fr' ? 'Recus, financement, remittance et paiements de services.' : 'Receipts, funding, remittance, and service payments.'}</Typography>
                                    </Box>
                                    <Chip label={lang === 'fr' ? `${filteredItems.length} element${filteredItems.length === 1 ? '' : 's'}` : `${filteredItems.length} item${filteredItems.length === 1 ? '' : 's'}`} sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, bgcolor: 'rgba(255,138,0,0.14)', color: BRAND_ORANGE, fontWeight: 800 }} />
                                </Stack>

                                <Grid container spacing={1.5} sx={{ mb: 2.2 }}>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth value={query} onChange={(event) => setQuery(event.target.value)} placeholder={lang === 'fr' ? 'Rechercher recu, ID, client, ou service' : 'Search receipt, ID, customer, or service'} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,0.55)' }} /></InputAdornment>) }} sx={{ '& .MuiOutlinedInput-root': { color: '#fff', borderRadius: 4, background: SURFACE_SOFT, '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' } } }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField select fullWidth value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} label={lang === 'fr' ? 'Statut' : 'Status'} sx={{ '& .MuiOutlinedInput-root': { color: '#fff', borderRadius: 4, background: SURFACE_SOFT, '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' } } }}>
                                            <MenuItem value="all">{lang === 'fr' ? 'Tous statuts' : 'All status'}</MenuItem>
                                            <MenuItem value="completed">{lang === 'fr' ? 'Termine' : 'Completed'}</MenuItem>
                                            <MenuItem value="pending">{lang === 'fr' ? 'En attente' : 'Pending'}</MenuItem>
                                            <MenuItem value="failed">{lang === 'fr' ? 'Echec' : 'Failed'}</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField select fullWidth value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} label={lang === 'fr' ? 'Type' : 'Type'} sx={{ '& .MuiOutlinedInput-root': { color: '#fff', borderRadius: 4, background: SURFACE_SOFT, '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' } } }}>
                                            {typeFilterOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {lang === 'fr' ? option.fr : option.en}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <Stack spacing={1.5}>
                                    {filteredItems.map((item) => {
                                        const meta = getTypeMeta(item.type, lang)
                                        const currency = String(item.currency || fallbackCurrency).toUpperCase()
                                        const rowStatusLabel = statusLabel(item.status, lang)
                                        const rowStatusKey = String(item.status || 'completed').toLowerCase()

                                        return (
                                            <Box
                                                key={item.id}
                                                onClick={() => navigate(`/receipt/${encodeURIComponent(item.id)}`, { state: { transaction: item } })}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 4,
                                                    background: 'rgba(255,255,255,0.03)',
                                                    border: '1px solid rgba(255,255,255,0.06)',
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.18s ease, border-color 0.18s ease, background 0.18s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-1px)',
                                                        borderColor: 'rgba(255,138,0,0.35)',
                                                        background: 'rgba(255,255,255,0.05)',
                                                    },
                                                }}
                                            >
                                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                        <Avatar sx={{ bgcolor: `${meta.accent}22`, color: meta.accent, width: 48, height: 48 }}>{meta.icon}</Avatar>
                                                        <Box>
                                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                                <Typography sx={{ color: '#fff', fontWeight: 800 }}>{localizeTransactionText(item.counterpart || item.note || meta.label, lang)}</Typography>
                                                                <Chip label={rowStatusLabel} size="small" sx={{ bgcolor: rowStatusKey === 'completed' ? 'rgba(54,211,153,0.14)' : 'rgba(255,138,0,0.14)', color: rowStatusKey === 'completed' ? '#36D399' : BRAND_ORANGE, fontWeight: 700 }} />
                                                            </Stack>
                                                            <Typography sx={{ color: 'rgba(255,255,255,0.62)', mt: 0.5 }}>{localizeTransactionText(item.note || meta.description, lang)}</Typography>
                                                            <Stack direction="row" spacing={1.2} sx={{ mt: 0.9 }} flexWrap="wrap">
                                                                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12.5 }}>{item.id}</Typography>
                                                                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12.5 }}>{formatDate(item.createdAt, lang)}</Typography>
                                                                <Typography sx={{ color: meta.accent, fontSize: 12.5, fontWeight: 700 }}>
                                                                    {lang === 'fr' ? 'Ouvrir le recu' : 'Open receipt'}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                    </Stack>

                                                    <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>{formatMoney(item.amount, currency)}</Typography>
                                                        <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 13 }}>{lang === 'fr' ? 'Frais ' : 'Fee '}{formatMoney(item.fee, currency)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        )
                                    })}

                                    {filteredItems.length === 0 && (
                                        <Box sx={{ p: 3, borderRadius: 4, textAlign: 'center', background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.12)' }}>
                                            <Typography sx={{ color: '#fff', fontWeight: 800, mb: 0.8 }}>{lang === 'fr' ? 'Aucune activite correspondante pour le moment' : 'No matching activity yet'}</Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>{lang === 'fr' ? 'Essayez une autre recherche ou envoyez un paiement pour commencer votre piste de preuve.' : 'Try another search or send a payment to start building your proof trail.'}</Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
                                        <ShieldIcon sx={{ color: BRAND_ORANGE }} />
                                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>{lang === 'fr' ? 'Normes de preuve' : 'Proof standards'}</Typography>
                                    </Stack>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.68)', mb: 2 }}>{lang === 'fr' ? 'Chaque piste de paiement doit sembler prete pour audit et facile a comprendre, meme sur des connexions plus lentes.' : 'Every payment trail should feel audit-ready and easy to understand, even on slower connections.'}</Typography>
                                    <Stack spacing={1.1}>
                                        {[
                                            lang === 'fr' ? 'Les recus restent groupes par type de service.' : 'Receipts stay grouped by service type.',
                                            lang === 'fr' ? 'Les frais et statuts restent visibles sans ouvrir chaque ligne.' : 'Fees and status remain visible without opening each row.',
                                            lang === 'fr' ? 'Le centre d aide est a un clic pour les preuves manquantes ou paiements echoues.' : 'Help Center is one click away for missing proof or failed payments.',
                                        ].map((point) => (
                                            <Box key={point} sx={{ px: 1.4, py: 1.2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 13.5 }}>{point}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.5 }}>{lang === 'fr' ? 'Suivi rapide' : 'Quick follow-up'}</Typography>
                                    <Stack spacing={1.2}>
                                        <Button variant="contained" fullWidth onClick={() => navigate('/send')} sx={{ borderRadius: 999, py: 1.2, textTransform: 'none', fontWeight: 900, bgcolor: BRAND_ORANGE }}>{lang === 'fr' ? 'Envoyer de l argent' : 'Send Money'}</Button>
                                        <Button variant="outlined" fullWidth onClick={() => navigate('/payment')} sx={{ borderRadius: 999, py: 1.2, textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.14)' }}>{lang === 'fr' ? 'Payer un commerce' : 'Pay a business'}</Button>
                                        <Button variant="text" fullWidth onClick={() => navigate('/help-center')} sx={{ borderRadius: 999, py: 1.1, textTransform: 'none', fontWeight: 800, color: 'rgba(255,255,255,0.82)' }}>{lang === 'fr' ? 'Ouvrir le centre d aide' : 'Open Help Center'}</Button>
                                    </Stack>
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2 }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontSize: 13 }}>
                                        {liveTransactions.length > 0
                                            ? (lang === 'fr'
                                                ? 'Cette page lit maintenant votre historique reel de transactions.'
                                                : 'This page is now reading your live transaction history.')
                                            : (lang === 'fr'
                                                    ? 'Aucune transaction reelle n a encore ete enregistree pour ce compte.'
                                                    : 'No live transactions have been recorded for this account yet.')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default Transactions

